import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { ChevronUp, ChevronDown, Info } from 'lucide-react'
import { GroupedDocumentRow } from './GroupedDocumentRow'
import type { DocumentRecord } from '@/types/catalog'
import { RowAction } from '@/components/DataTable/DataTableTypes'
import { useTranslation } from '@/i18n'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import StatusTag, { DocumentStatus } from '@/components/StatusTag'
import { getDocumentStatus } from '@/utils/documentStatus'
import { formatFileSize } from '@/utils/fileSize'
import { timeAgo } from '@/utils/timeUtils'
import RowActionsDropdown from '@/components/DataTable/RowActionsDropdown'
import EmptyDataRow from '@/components/DataTable/EmptyDataRow'
import clsx from 'clsx'
import { cleanDocumentName } from '@/routes/IntelligentBoard/utils/stripGraphPrefix'

type ProcessedEntry = { type: 'group'; data: DocumentRecord[]; setId: string } | { type: 'individual'; data: DocumentRecord }

interface CatalogTableProps {
  cols: { id: string; label: string }[]
  groupedEntries: Array<{ type: 'group'; data: DocumentRecord[]; setId: string }>
  individualData: DocumentRecord[]
  orderedEntries?: ProcessedEntry[]
  onRowClick?: (dataItem: DocumentRecord) => void
  rowActions?: RowAction[]
  defaultSort?: Array<{ id: string; desc: boolean }>
  onSortChange?: (sorting: Array<{ id: string; desc: boolean }>) => void
  selectable?: boolean
  selectedRows?: string[]
  onRowSelect?: (id: string, checked: boolean) => void
  onSelectAll?: (checked: boolean) => void
  onRename?: (id: string, newName: string) => void
  loading?: boolean
  showEmptyMessage?: boolean
  emptyMessage?: string
  disabledRows?: string[]
  onOpenSet?: (documents: DocumentRecord[]) => void
  onRenameGroup?: (setId: string, newName: string) => void
  onManageFiles?: (setId: string, documents: DocumentRecord[]) => void
  onDeleteGroup?: (setId: string, documents: DocumentRecord[]) => void
  /** Pin the header row while the table body scrolls (FS-534 infinite-scroll modal). */
  stickyHeader?: boolean
}

export const CatalogTable: React.FC<CatalogTableProps> = ({
  cols,
  groupedEntries,
  individualData,
  orderedEntries,
  onRowClick,
  rowActions,
  defaultSort,
  onSortChange,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onSelectAll,
  onRename,
  loading = false,
  showEmptyMessage = true,
  emptyMessage,
  disabledRows = [],
  onOpenSet,
  onRenameGroup,
  onManageFiles,
  onDeleteGroup,
  stickyHeader = false,
}) => {
  const { t } = useTranslation()
  const [sorting, setSorting] = React.useState<Array<{ id: string; desc: boolean }>>(defaultSort || [])

  // Re-render every 60 seconds to keep relative times (e.g., "10 mins ago") fresh
  const [, setTick] = React.useState(0)
  React.useEffect(() => {
    const interval = setInterval(() => setTick((prev) => prev + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  const handleSort = (column: string) => {
    const currentSort = sorting.find((s) => s.id === column)
    const newSorting: Array<{ id: string; desc: boolean }> = currentSort
      ? [{ id: column, desc: !currentSort.desc }]
      : [{ id: column, desc: false }]

    setSorting(newSorting)
    if (onSortChange) {
      onSortChange(newSorting)
    }
  }

  const renderSortableHeader = (col: { id: string; label: string }) => {
    if (col.label.toLowerCase().includes('actions')) {
      return col.label
    }

    const currentSort = sorting.find((s) => s.id === col.id)

    return (
      <button
        className="flex items-center gap-1 hover:underline font-medium"
        onClick={() => handleSort(col.id)}
      >
        {col.label}
        {currentSort &&
          (currentSort.desc ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronUp className="w-4 h-4" />
          ))}
      </button>
    )
  }

  const allSelectableItems = [
    ...groupedEntries.flatMap(g => g.data.map(d => d.id)),
    ...individualData.map(d => d.id)
  ].filter(id => !disabledRows.includes(id))

  const selectedCount = allSelectableItems.filter(id => selectedRows.includes(id)).length
  const allSelected = allSelectableItems.length > 0 && selectedCount === allSelectableItems.length
  const someSelected = selectedCount > 0 && selectedCount < allSelectableItems.length

  const renderIndividualRow = (dataItem: DocumentRecord) => {
    const { id, type, name: rawName, created_at } = dataItem
    // Strip legacy "P&ID Graph "/"PFD Graph " marker, and the bare
    // "P&ID "/"PFD " marker on pnid/pfd-typed rows, before display.
    const name = cleanDocumentName(rawName, type)
    const status = getDocumentStatus(dataItem)
    const isDisabled = disabledRows.includes(id)

    return (
      <TableRow
        key={id}
        className={clsx(
          !isDisabled &&
            dataItem.status !== 'uploading' &&
            dataItem.is_indexed &&
            'cursor-pointer',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )}
        onClick={(e) => {
          if (isDisabled) return
          const target = e.target as HTMLElement
          if (target.closest('input[type="checkbox"], button, [role="button"]')) return
          if (onRowClick) {
            onRowClick(dataItem)
          }
        }}
      >
        {selectable && selectedRows && onRowSelect && (
          <TableCell className="pl-3">
            <Checkbox
              id={id}
              checked={selectedRows.includes(id)}
              onCheckedChange={(checked) => onRowSelect(id, checked === true)}
              disabled={isDisabled}
            />
          </TableCell>
        )}
        {cols.map((col) => {
          if (col.id === 'name') {
            return (
              <TableCell key={col.id} className={clsx(
                !isDisabled &&
                  dataItem.status !== 'uploading' &&
                  dataItem.is_indexed &&
                  'hover:underline'
              )}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold truncate min-w-0">
                        {name}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{name}</p>
                  </TooltipContent>
                </Tooltip>
              </TableCell>
            )
          }

          if (col.id === 'type') {
            return (
              <TableCell key={col.id}>
                <span>{t(`data_catalog:doc_types.${type}`)}</span>
              </TableCell>
            )
          }

          if (col.id === 'status') {
            const hasError = dataItem.error_message && status === DocumentStatus.failed
            return (
              <TableCell key={col.id}>
                {hasError ? (
                  <div className="flex items-center gap-1.5">
                    <StatusTag status={status} />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md">
                          <p>{dataItem.error_message}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  <StatusTag status={status} />
                )}
              </TableCell>
            )
          }

          // NOTE: The "Date Created" column (col.id === 'created_at') actually displays
          // upload_started_at, not created_at. The column ID remains 'created_at' for
          // backward compatibility, but the displayed value and sort order use upload_started_at.
          if (col.id === 'created_at' || col.id === 'updated_at') {
            return (
              <TableCell key={col.id}>
                {timeAgo(
                  col.id === 'updated_at'
                    ? dataItem.updated_at || created_at
                    : dataItem.upload_started_at || created_at
                )}
              </TableCell>
            )
          }

          if (col.id === 'file_size') {
            return (
              <TableCell key={col.id}>
                {formatFileSize(dataItem.file_size)}
              </TableCell>
            )
          }

          if (col.id === 'uploaded_by') {
            const uploaderName = dataItem.uploader_name || dataItem.user_id || t('common:unknown_user')
            const displayName = uploaderName.length > 30
              ? uploaderName.substring(0, 30) + '...'
              : uploaderName
            return (
              <TableCell key={col.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="truncate">
                      {displayName}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{uploaderName}</p>
                  </TooltipContent>
                </Tooltip>
              </TableCell>
            )
          }

          return null
        })}
        {rowActions && (
          <TableCell className="pr-5">
            <RowActionsDropdown
              id={id}
              actions={rowActions}
              rowType={dataItem.type}
              document={dataItem}
              onRename={onRename ? (newName) => onRename(id, newName) : undefined}
            />
          </TableCell>
        )}
      </TableRow>
    )
  }

  return (
    <TooltipProvider>
      <Table className="table-fixed">
        <TableHeader className={clsx('bg-slate-50 dark:bg-dark-muted', stickyHeader && 'sticky top-0 z-10')}>
          <TableRow>
            {selectable && (
              <TableHead className="w-12 pl-3">
                <Checkbox
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={(checked) => {
                    if (onSelectAll) {
                      onSelectAll(checked === true)
                    }
                  }}
                />
              </TableHead>
            )}
            {cols.map((col) => (
              <TableHead
                key={col.id}
                className={
                  col.id === 'name' ? 'w-full' :
                  col.id === 'type' ? 'w-[140px]' :
                  col.id === 'file_size' ? 'w-[120px]' :
                  col.id === 'uploaded_by' ? 'w-[200px]' :
                  'w-[180px]'
                }
              >
                {renderSortableHeader(col)}
              </TableHead>
            ))}
            {rowActions && (
              <TableHead className="w-16 pr-5">
                {t('common:table_columns.actions')}
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedEntries.length === 0 && individualData.length === 0 ? (
            <EmptyDataRow
              colSpan={cols.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}
              loading={loading}
              showMessage={showEmptyMessage}
              message={emptyMessage || t('data_catalog:no_documents')}
            />
          ) : (
            <>
              {orderedEntries ? (
                // Render in sort-preserving interleaved order
                orderedEntries.map((entry) =>
                  entry.type === 'group' ? (
                    <GroupedDocumentRow
                      key={entry.setId}
                      setId={entry.setId}
                      documents={entry.data}
                      onOpenSet={onOpenSet}
                      onRowClick={onRowClick}
                      selectedRows={selectedRows}
                      onRowSelect={onRowSelect}
                      selectable={selectable}
                      cols={cols}
                      onRenameGroup={onRenameGroup}
                      onManageFiles={onManageFiles}
                      onDeleteGroup={onDeleteGroup}
                      showActions={!!(rowActions || onRenameGroup || onManageFiles || onDeleteGroup || onOpenSet)}
                      disabledRows={disabledRows}
                    />
                  ) : (
                    renderIndividualRow(entry.data as DocumentRecord)
                  )
                )
              ) : (
                // Fallback: grouped first, then individual
                <>
                  {groupedEntries.map((group) => (
                    <GroupedDocumentRow
                      key={group.setId}
                      setId={group.setId}
                      documents={group.data}
                      onOpenSet={onOpenSet}
                      onRowClick={onRowClick}
                      selectedRows={selectedRows}
                      onRowSelect={onRowSelect}
                      selectable={selectable}
                      cols={cols}
                      onRenameGroup={onRenameGroup}
                      onManageFiles={onManageFiles}
                      onDeleteGroup={onDeleteGroup}
                      showActions={!!(rowActions || onRenameGroup || onManageFiles || onDeleteGroup || onOpenSet)}
                      disabledRows={disabledRows}
                    />
                  ))}
                  {individualData.map(renderIndividualRow)}
                </>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TooltipProvider>
  )
}
